/**
 * Timeout Management Utility
 * Provides managed timeout functionality to prevent memory leaks
 * and ensure proper cleanup of asynchronous operations.
 */

class TimeoutManager {
  constructor() {
    this.pendingTimeouts = new Set();
  }

  /**
   * Creates a managed timeout that can be tracked and cleaned up
   * @param {Function} callback - The function to execute after the delay
   * @param {number} delay - Delay in milliseconds
   * @param {string} label - Optional label for debugging
   * @returns {number} - The timeout ID
   */
  setTimeout(callback, delay, label = "") {
    const timeoutId = setTimeout(() => {
      this.pendingTimeouts.delete(timeoutId);
      if (label) {
        console.log(
          `[${new Date().toISOString()}] ⏰ Managed timeout executed: ${label}`,
        );
      }
      callback();
    }, delay);

    this.pendingTimeouts.add(timeoutId);

    if (label) {
      console.log(
        `[${new Date().toISOString()}] ⏰ Managed timeout scheduled: ${label} (${delay}ms)`,
      );
    }

    return timeoutId;
  }

  /**
   * Clears a specific managed timeout
   * @param {number} timeoutId - The timeout ID to clear
   * @param {string} label - Optional label for debugging
   * @returns {boolean} - True if timeout was found and cleared
   */
  clearTimeout(timeoutId, label = "") {
    if (this.pendingTimeouts.has(timeoutId)) {
      clearTimeout(timeoutId);
      this.pendingTimeouts.delete(timeoutId);
      if (label) {
        console.log(
          `[${new Date().toISOString()}] 🧹 Managed timeout cleared: ${label}`,
        );
      }
      return true;
    }
    if (label) {
      console.log(
        `[${new Date().toISOString()}] ⚠️  Timeout not found for clearing: ${label}`,
      );
    }
    return false;
  }

  /**
   * Clears all pending managed timeouts
   * @param {string} context - Optional context for logging
   */
  clearAllTimeouts(context = "") {
    const count = this.pendingTimeouts.size;
    this.pendingTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.pendingTimeouts.clear();

    if (context) {
      console.log(
        `[${new Date().toISOString()}] 🧹 Cleared ${count} managed timeouts for: ${context}`,
      );
    }
  }

  /**
   * Gets the count of currently pending timeouts
   * @returns {number} - Number of pending timeouts
   */
  getPendingCount() {
    return this.pendingTimeouts.size;
  }

  /**
   * Checks if a timeout ID is currently managed
   * @param {number} timeoutId - The timeout ID to check
   * @returns {boolean} - True if the timeout is being managed
   */
  hasTimeout(timeoutId) {
    return this.pendingTimeouts.has(timeoutId);
  }
}

// Create singleton instance for global use
const timeoutManager = new TimeoutManager();

export default timeoutManager;
export { TimeoutManager };
